/**
 * Suitability rules — the single source of truth for product suitability,
 * shared by the web custom-portfolio builder (which decorates the verdict with
 * display strings) and the API dynamic-portfolio generator. Rules:
 * 1. Professional / Eligible Counterparty classification bypasses all checks
 *    (a missing classification does NOT bypass).
 * 2. No experience with the product type → not suitable.
 * 3. Goal risk level order < product's minimum risk level order → not suitable.
 * 4. Goal risk score < product's minimum risk score → not suitable (QL-10).
 *    Independent of rule 3 — a product may pin either, both, or neither, and a
 *    product failing both reports both issues.
 * 5. Goal time horizon order < product's minimum time horizon order → not
 *    suitable.
 * A rule with missing data on either side of its comparison is neutral (never
 * fires). Orders and scores compare with strict `<`; 0 is a legitimate order
 * and a legitimate score value.
 */

export type SuitabilityIssueType =
  | "no_experience"
  | "risk_too_low"
  | "risk_score_too_low"
  | "time_horizon_too_short";

/**
 * Structural subsets of the web snapshot types (`ClientClassificationSnapshot`
 * / `ProductAssessmentSnapshot`) — on the API side the values arrive as JSON
 * parsed from `flow_instance_component_value`, so every field is optional.
 */
export type SuitabilityClassification = { classificationType?: string };

export type SuitabilityExperience = {
  experiences?: Array<{ productTypeId: string; hasExperience?: boolean }>;
};

export type SuitabilityContext = {
  classification: SuitabilityClassification | null;
  experience: SuitabilityExperience | null;
  goalRiskLevelOrder?: number;
  /** QL-10 — the goal's FINAL risk score (matrix + adjustment, after the compliance cap). */
  goalRiskScore?: number;
  goalTimeHorizonOrder?: number;
};

export type ProductSuitabilityMeta = {
  productTypeId: string | null;
  minRiskLevelOrder?: number;
  /** QL-10 — minimum goal risk score. Undefined = not configured; 0 is a real score. */
  minRiskScore?: number;
  minTimeHorizonOrder?: number;
};

export type ProductSuitabilityResult = {
  isSuitable: boolean;
  issues: SuitabilityIssueType[];
};

/**
 * Check if a client bypasses suitability checks based on classification.
 * Professional and Eligible Counterparty clients can access all products.
 */
export function bypassesSuitabilityChecks(
  classification: SuitabilityClassification | null,
): boolean {
  if (!classification) return false;

  return (
    classification.classificationType === "Professional" ||
    classification.classificationType === "Eligible Counterparty"
  );
}

/** Check if a product is suitable for a client based on their context. */
export function checkProductSuitability(
  product: ProductSuitabilityMeta,
  context: SuitabilityContext,
): ProductSuitabilityResult {
  const issues: SuitabilityIssueType[] = [];

  // Rule 1: Professional/Eligible Counterparty bypass all checks
  if (bypassesSuitabilityChecks(context.classification)) {
    return { isSuitable: true, issues: [] };
  }

  // Rule 2: Check product assessment. `hasExperience` is the category verdict:
  // the plain declaration for product types without an assessment, or the
  // assessment pass (summed score >= minScore) for assessed types. A missing
  // entry counts as no experience.
  if (product.productTypeId && context.experience) {
    const entry = (context.experience.experiences ?? []).find(
      (exp) => exp.productTypeId === product.productTypeId,
    );
    if (!entry?.hasExperience) {
      issues.push("no_experience");
    }
  }

  // Rule 3: Check risk level (using order for comparison). Only check if both
  // values are defined (product has requirement AND client has assessment).
  if (product.minRiskLevelOrder !== undefined && context.goalRiskLevelOrder !== undefined) {
    if (context.goalRiskLevelOrder < product.minRiskLevelOrder) {
      issues.push("risk_too_low");
    }
  }

  // Rule 4: Check the minimum risk score (QL-10). Independent of rule 3 — a
  // product may fail both and report both. Only fires when both sides are set;
  // `undefined` means "not configured / not known" while 0 is a real score.
  if (product.minRiskScore !== undefined && context.goalRiskScore !== undefined) {
    if (context.goalRiskScore < product.minRiskScore) {
      issues.push("risk_score_too_low");
    }
  }

  // Rule 5: Check time horizon (using order for comparison). Only check if
  // both values are defined.
  if (product.minTimeHorizonOrder !== undefined && context.goalTimeHorizonOrder !== undefined) {
    if (context.goalTimeHorizonOrder < product.minTimeHorizonOrder) {
      issues.push("time_horizon_too_short");
    }
  }

  return { isSuitable: issues.length === 0, issues };
}

/** The three labels the builder shows in its Suitable column. */
export type SuitabilityStatus = "suitable" | "unsuitable" | "unknown";

/**
 * The verdict AND the label the builder shows (QL-707: one definition for the
 * web table and the frozen advice-time record). Professional / Eligible
 * Counterparty is always suitable; a failing rule is unsuitable; a product that
 * pins a requirement the client's context cannot answer (no risk assessment, no
 * horizon, no experience data) is unknown rather than suitable.
 */
export function deriveSuitabilityStatus(
  product: ProductSuitabilityMeta,
  context: SuitabilityContext,
): { status: SuitabilityStatus; result: ProductSuitabilityResult } {
  const result = checkProductSuitability(product, context);
  if (bypassesSuitabilityChecks(context.classification)) return { status: "suitable", result };
  if (!result.isSuitable) return { status: "unsuitable", result };
  const unanswered =
    (product.minRiskLevelOrder !== undefined && context.goalRiskLevelOrder === undefined) ||
    (product.minRiskScore !== undefined && context.goalRiskScore === undefined) ||
    (product.minTimeHorizonOrder !== undefined && context.goalTimeHorizonOrder === undefined) ||
    (!!product.productTypeId && !context.experience);
  return { status: unanswered ? "unknown" : "suitable", result };
}
